<template>
  <n-button
    class="report-download-button"
    variant="secondary"
    :loading="isDownloadInProgress"
    :disabled="isDownloadInProgress || isReportLoading"
    @click="downloadReport"
    >Download
  </n-button>
</template>

<script lang="ts" setup>
import { ReportCsvDownloadResponse, reportDownloaderService } from '../services/reportDownloader';
import { saveAs } from 'file-saver';
import Toast from '@/shared/services/toast';
import { SET_REPORT_LOADING } from '@/store/reports/mutationTypes';
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const selectedDateOption = computed(() => store.getters.selectedDateOption);
const selectedFlowId = computed(() => store.getters.selectedFlowId);
const isReportLoading = computed(() => store.getters.isReportLoading);
const setReportLoading = (loading: boolean) => store.commit(SET_REPORT_LOADING, loading);

const isDownloadInProgress = ref(false);

const activeRoute = useRoute();

async function downloadReport() {
  try {
    isDownloadInProgress.value = true;
    setReportLoading(true);
    const report = await getReport();
    if (report) {
      saveAs(report.data, report.fileName);
    }
  } catch (error: any) {
    Toast.showError(`Failed to download report: ${error.message}`);
  } finally {
    setReportLoading(false);
    isDownloadInProgress.value = false;
  }
}

async function getReport(): Promise<ReportCsvDownloadResponse | null> {
  const currentRoute = typeof activeRoute.name === 'string' ? activeRoute.name : String(activeRoute.name ?? '');

  if (currentRoute === 'reports-overview') {
    return await reportDownloaderService.getMessagingReportCsv(currentWorkspace.value.id, selectedDateOption.value);
  }
  if (currentRoute === 'reports-flows') {
    return reportDownloaderService.getFlowsReportCsv(
      currentWorkspace.value.id,
      selectedFlowId.value,
      selectedDateOption.value,
    );
  }
  if (currentRoute === 'reports-intents') {
    return reportDownloaderService.getNLPReportCsv(currentWorkspace.value.id, selectedDateOption.value);
  }
  if (currentRoute === 'reports-usage') {
    return reportDownloaderService.getUsageReportCsv(currentWorkspace.value.id, selectedDateOption.value);
  }
  return null;
}
</script>

<style lang="scss" scoped>
.report-download-button {
  &.btn--secondary {
    padding: 7px 16px;
  }
}
</style>
